import { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { PAGE_TYPE, NUMERIC_FIELDS, encodeMapShare, encodeTabletShare, decodeShareCode } from "../util/shareCode";
import { setInputValue } from "../util/setInputValue";

function hasMapSelection(pageType, result) {
  const fields = NUMERIC_FIELDS[pageType];
  return (result.wantMod?.length || 0) > 0
    || (result.ignoreMod?.length || 0) > 0
    || fields.some((f) => result[f]);
}

function readRawFieldValues(pageType) {
  const rawFieldValues = {};
  NUMERIC_FIELDS[pageType].forEach((f) => {
    const el = document.getElementById(f);
    if (el && el.value) rawFieldValues[f] = el.value;
  });
  return rawFieldValues;
}

function writeRawFieldValues(rawFields) {
  Object.entries(rawFields || {}).forEach(([field, value]) => {
    setInputValue(field, value);
  });
}

function hasTabletSelection(result) {
  return (result.orMod?.length || 0) > 0
    || (result.andMod?.length || 0) > 0
    || (result.category?.length || 0) > 0;
}

function ShareCodeBar({ pageType, result, regexOperator, lvList, onLoad }) {
  const [shareOpen, setShareOpen] = useState(false);
  const [shareCode, setShareCode] = useState('');
  const [loadCode, setLoadCode] = useState('');
  const [generating, setGenerating] = useState(false);

  async function handleShare() {
    const isTablet = pageType === PAGE_TYPE.TABLET;
    const hasSelection = isTablet ? hasTabletSelection(result) : hasMapSelection(pageType, result);
    if (!hasSelection) {
      alert('공유할 선택 항목이 없습니다.');
      return;
    }

    setGenerating(true);
    try {
      const code = isTablet
        ? await encodeTabletShare(result, lvList)
        : await encodeMapShare(pageType, result, regexOperator, readRawFieldValues(pageType));
      setShareCode(code);
      setShareOpen(true);
    } catch (error) {
      console.error('Failed to generate share code:', error);
      alert('공유 코드 생성에 실패했습니다.');
    } finally {
      setGenerating(false);
    }
  }

  async function copyShareCode() {
    try {
      await navigator.clipboard.writeText(shareCode);
      alert('복사 완료!');
    } catch {
      alert('복사 실패!');
    }
  }

  async function handleLoadCode() {
    const code = loadCode.trim();
    if (!code) return;

    try {
      const decoded = await decodeShareCode(code, lvList);
      if (decoded.pageType !== pageType) {
        alert('이 페이지용 공유 코드가 아닙니다.');
        return;
      }
      onLoad(decoded.result, decoded.regexOperator);
      writeRawFieldValues(decoded.rawFields);
      setLoadCode('');
      alert('불러왔습니다!');
    } catch (error) {
      console.error('Failed to decode share code:', error);
      alert('코드를 불러올 수 없습니다. 코드를 다시 확인해주세요.');
    }
  }

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ mb: 2 }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <Button variant="outlined" onClick={handleShare} disabled={generating}>
          공유 코드 생성
        </Button>
        <TextField
          size="small"
          placeholder="공유 코드 붙여넣기"
          value={loadCode}
          onChange={(e) => setLoadCode(e.target.value)}
          sx={{ flex: 1 }}
        />
        <Button variant="outlined" onClick={handleLoadCode} disabled={!loadCode.trim()}>
          코드 불러오기
        </Button>
      </Stack>

      <Dialog open={shareOpen} onClose={() => setShareOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>공유 코드</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            아래 코드를 복사해서 공유하세요. 상대방은 이 페이지의 "공유 코드 붙여넣기"에 붙여넣고 불러오면 됩니다.
          </Typography>
          <TextField
            fullWidth
            multiline
            value={shareCode}
            InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShareOpen(false)}>닫기</Button>
          <Button variant="contained" onClick={copyShareCode}>복사</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ShareCodeBar;
